import React, { useState } from "react";
import Header from "../components/Header";
import Toast from "../components/Toast";
import { gstReportService } from "../services/auditService";

// ── Date helpers ──────────────────────────────────────────────────────────────
const fmt = (d) => d.toISOString().slice(0, 10);

const quickRanges = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();

  const thisMonth = {
    start: fmt(new Date(y, m, 1)),
    end: fmt(new Date(y, m + 1, 0)),
  };
  const lastMonth = {
    start: fmt(new Date(y, m - 1, 1)),
    end: fmt(new Date(y, m, 0)),
  };

  // Previous GST quarter (Apr-Jun, Jul-Sep, Oct-Dec, Jan-Mar)
  const qStart = [0, 3, 6, 9];
  const qIdx = qStart.filter((s) => s <= m).length - 1;
  const prevQStart = qIdx === 0 ? new Date(y - 1, 9, 1) : new Date(y, qStart[qIdx - 1 < 0 ? 3 : qIdx - 1], 1);
  const prevQEnd = new Date(prevQStart.getFullYear(), prevQStart.getMonth() + 3, 0);
  const lastQuarter = { start: fmt(prevQStart), end: fmt(prevQEnd) };

  // Indian FY: Apr 1 – Mar 31
  const fyStart = m >= 3 ? new Date(y, 3, 1) : new Date(y - 1, 3, 1);
  const fyEnd = new Date(fyStart.getFullYear() + 1, 2, 31);
  const thisFY = { start: fmt(fyStart), end: fmt(fyEnd) };

  return { thisMonth, lastMonth, lastQuarter, thisFY };
};

// ── Number formatter ──────────────────────────────────────────────────────────
const fmtAmt = (v) => `₹${Number(v || 0).toFixed(2)}`;
const sumCol = (rows, key) => rows.reduce((s, r) => s + Number(r[key] || 0), 0);

// ── Pagination component ──────────────────────────────────────────────────────
const Pagination = ({ page, totalPages, onChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-1 px-4 py-2 border-t border-gray-100 bg-white">
      <button onClick={() => onChange(0)} disabled={page === 0} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">«</button>
      <button onClick={() => onChange(page - 1)} disabled={page === 0} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">‹</button>
      <span className="text-xs text-gray-500 px-2">Page {page + 1} / {totalPages}</span>
      <button onClick={() => onChange(page + 1)} disabled={page >= totalPages - 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">›</button>
      <button onClick={() => onChange(totalPages - 1)} disabled={page >= totalPages - 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">»</button>
    </div>
  );
};

// ── Column-total footer row ───────────────────────────────────────────────────
const TotalRow = ({ cols, rows, span = 0 }) => (
  <tfoot>
    <tr className="bg-gradient-to-r from-teal-50 to-indigo-50 border-t-2 border-indigo-200 font-bold text-xs">
      {span > 0 && <td colSpan={span} className="py-2 px-3 text-right text-gray-600">Total</td>}
      {cols.map((key) => (
        <td key={key} className="py-2 px-3 text-right">{fmtAmt(sumCol(rows, key))}</td>
      ))}
    </tr>
  </tfoot>
);

// ── Main Component ────────────────────────────────────────────────────────────
const GstReport = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("finalized");
  const [activeTab, setActiveTab] = useState("summary");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [toast, setToast] = useState(null);
  const [report, setReport] = useState(null);
  const [salesPage, setSalesPage] = useState(0);
  const [purchasePage, setPurchasePage] = useState(0);
  const [returnsPage, setReturnsPage] = useState(0);
  const PAGE_SIZE = 50;

  const includeCancel = invoiceStatus === "all";

  const applyQuick = (range) => {
    const r = quickRanges();
    const { start, end } = r[range];
    setStartDate(start);
    setEndDate(end);
  };

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      setToast({ type: "error", message: "Please select both start and end dates" });
      return;
    }
    if (startDate > endDate) {
      setToast({ type: "error", message: "From date must be on or before To date" });
      return;
    }
    setLoading(true);
    setReport(null);
    setSalesPage(0); setPurchasePage(0); setReturnsPage(0);
    try {
      const [summary, salesData, purchasesData, returnsData, hsnData] = await Promise.all([
        gstReportService.getGstSummary(startDate, endDate, includeCancel),
        gstReportService.getGstSales(startDate, endDate, includeCancel, 0, PAGE_SIZE),
        gstReportService.getGstPurchases(startDate, endDate, 0, PAGE_SIZE),
        gstReportService.getGstReturns(startDate, endDate, includeCancel, 0, PAGE_SIZE),
        gstReportService.getGstHsnSummary(startDate, endDate, includeCancel),
      ]);
      setReport({ summary, sales: salesData, purchases: purchasesData, returns: returnsData, hsn: hsnData });
      setActiveTab("summary");
    } catch (err) {
      setToast({ type: "error", message: err.response?.data?.message || "Failed to fetch GST report" });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (tab, newPage) => {
    if (!startDate || !endDate) return;
    try {
      if (tab === "sales") {
        const data = await gstReportService.getGstSales(startDate, endDate, includeCancel, newPage, PAGE_SIZE);
        setReport((r) => ({ ...r, sales: data }));
        setSalesPage(newPage);
      } else if (tab === "purchases") {
        const data = await gstReportService.getGstPurchases(startDate, endDate, newPage, PAGE_SIZE);
        setReport((r) => ({ ...r, purchases: data }));
        setPurchasePage(newPage);
      } else if (tab === "returns") {
        const data = await gstReportService.getGstReturns(startDate, endDate, includeCancel, newPage, PAGE_SIZE);
        setReport((r) => ({ ...r, returns: data }));
        setReturnsPage(newPage);
      }
    } catch { setToast({ type: "error", message: "Failed to load page" }); }
  };

  const handleDownloadExcel = async () => {
    if (!startDate || !endDate) return;
    setDownloading("excel");
    try {
      const blob = await gstReportService.downloadGstReportExcel(startDate, endDate, includeCancel);
      triggerDownload(blob, `GST_Report_${startDate}_${endDate}.xlsx`);
    } catch { setToast({ type: "error", message: "Failed to download Excel" }); }
    finally { setDownloading(null); }
  };

  const handleDownloadPdf = async () => {
    if (!startDate || !endDate) return;
    setDownloading("pdf");
    try {
      const blob = await gstReportService.downloadGstReportPdf(startDate, endDate, includeCancel);
      triggerDownload(blob, `GST_Report_${startDate}_${endDate}.pdf`);
    } catch { setToast({ type: "error", message: "Failed to download PDF" }); }
    finally { setDownloading(null); }
  };

  const summary   = report?.summary   || {};
  const salesRows = report?.sales?.content     || [];
  const purRows   = report?.purchases?.content || [];
  const retRows   = report?.returns?.content   || [];
  const hsnRows   = report?.hsn?.hsnSummary    || [];

  const TABS = [
    { key: "summary",  label: "Summary"  },
    { key: "sales",    label: "Sales"    },
    { key: "purchase", label: "Purchase" },
    { key: "returns",  label: "Returns"  },
    { key: "hsn",      label: "HSN/Rate" },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-4">
      <Header showSearch={false} showCart={false} />
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <div className="flex-1 overflow-y-auto mt-4 space-y-4">

        {/* Page Header */}
        <div className="bg-white rounded-xl shadow-md border border-blue-200 p-4 flex items-center gap-3">
          <i className="fas fa-file-invoice-dollar text-3xl text-blue-600" />
          <div>
            <h1 className="text-xl font-bold text-blue-800">GST Reports</h1>
            <p className="text-xs text-gray-500">JashBill · GSTR-1 style outward supply summary</p>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-xl shadow-md border border-blue-200 p-4 space-y-3">
          {/* Quick-select */}
          <div className="flex flex-wrap gap-2">
            {[
              { label: "This Month",    range: "thisMonth"   },
              { label: "Last Month",    range: "lastMonth"   },
              { label: "Last Quarter",  range: "lastQuarter" },
              { label: "This FY",       range: "thisFY"      },
            ].map(({ label, range }) => (
              <button key={range} onClick={() => applyQuick(range)}
                className="px-3 py-1 text-xs rounded-full border border-indigo-300 text-indigo-700 hover:bg-indigo-50 transition">
                {label}
              </button>
            ))}
          </div>

          {/* Date pickers + status toggle + action buttons */}
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-1.5 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-1.5 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
            </div>

            {/* Invoice Status Toggle */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Invoice Status</label>
              <div className="flex gap-3 text-xs">
                {[
                  { val: "finalized", label: "Finalized only" },
                  { val: "all",       label: "Include cancelled" },
                ].map(({ val, label }) => (
                  <label key={val} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="invoiceStatus" value={val}
                      checked={invoiceStatus === val}
                      onChange={() => setInvoiceStatus(val)}
                      className="accent-indigo-600" />
                    <span className="text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 ml-auto">
              <button onClick={handleGenerate} disabled={loading}
                className={`flex items-center gap-2 px-5 py-1.5 rounded-full text-white font-semibold text-sm shadow transition ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"}`}>
                {loading ? <><i className="fas fa-spinner fa-spin" /> Generating...</> : <><i className="fas fa-search" /> Generate Report</>}
              </button>
              <button onClick={handleDownloadExcel} disabled={!!downloading || !report}
                className="flex items-center gap-2 px-4 py-1.5 rounded-full text-green-700 font-semibold text-sm border border-green-300 bg-green-50 hover:bg-green-100 transition disabled:opacity-40">
                {downloading === "excel" ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-file-excel" />} Export Excel ↓
              </button>
              <button onClick={handleDownloadPdf} disabled={!!downloading || !report}
                className="flex items-center gap-2 px-4 py-1.5 rounded-full text-red-700 font-semibold text-sm border border-red-300 bg-red-50 hover:bg-red-100 transition disabled:opacity-40">
                {downloading === "pdf" ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-file-pdf" />} Print / PDF ↓
              </button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-16">
            <i className="fas fa-spinner fa-spin text-blue-500 text-4xl" />
            <p className="mt-3 text-gray-500 text-sm">Generating GST report...</p>
          </div>
        )}

        {report && !loading && (
          <>
            {/* 4 Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Taxable Sales",           value: fmtAmt(summary.taxableSales),    icon: "fas fa-rupee-sign", color: "blue"   },
                { label: "Output GST",              value: fmtAmt(summary.outputGst),       icon: "fas fa-percentage", color: "indigo" },
                { label: "Taxable Purchases",       value: fmtAmt(summary.taxablePurchases),icon: "fas fa-truck",      color: "teal"   },
                { label: "Purchase GST Recorded",   value: fmtAmt(summary.purchaseGst),     icon: "fas fa-coins",      color: "green"  },
              ].map(({ label, value, icon, color }) => (
                <div key={label} className={`bg-white rounded-xl border border-${color}-200 shadow p-4 flex items-center gap-3`}>
                  <div className={`w-10 h-10 rounded-full bg-${color}-100 flex items-center justify-center flex-shrink-0`}>
                    <i className={`${icon} text-${color}-600`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className={`text-base font-bold text-${color}-700`}>{value}</p>
                  </div>
                </div>
              ))}
            </div>
            {summary.purchaseGst > 0 && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <i className="fas fa-info-circle mr-1" />
                Purchase GST is recorded from distributor invoices. ITC eligibility must be verified against GSTR-2A/2B.
              </p>
            )}

            {/* Tab Bar */}
            <div className="bg-white rounded-xl shadow-md border border-blue-200 overflow-hidden">
              <div className="flex border-b border-blue-200">
                {TABS.map(({ key, label }) => (
                  <button key={key} onClick={() => setActiveTab(key)}
                    className={`px-5 py-2.5 text-sm font-semibold transition ${activeTab === key ? "bg-indigo-600 text-white" : "text-gray-600 hover:bg-blue-50"}`}>
                    {label}
                  </button>
                ))}
              </div>

              {/* Tab 1: Summary */}
              {activeTab === "summary" && (
                <div className="p-4">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase">
                      <tr>
                        <th className="py-2 px-4 text-left">Tax Head</th>
                        <th className="py-2 px-4 text-right">Sales GST</th>
                        <th className="py-2 px-4 text-right">Purchase GST</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { head: "CGST", sales: summary.salesCgst,  pur: summary.purchaseCgst  },
                        { head: "SGST", sales: summary.salesSgst,  pur: summary.purchaseSgst  },
                        { head: "IGST", sales: summary.salesIgst,  pur: summary.purchaseIgst  },
                        { head: "Cess", sales: summary.salesCess,  pur: summary.purchaseCess  },
                      ].map(({ head, sales, pur }, i) => (
                        <tr key={head} className={`border-t ${i % 2 === 0 ? "bg-white" : "bg-blue-50"}`}>
                          <td className="py-2 px-4 font-medium text-gray-700">{head}</td>
                          <td className="py-2 px-4 text-right text-blue-700">{fmtAmt(sales)}</td>
                          <td className="py-2 px-4 text-right text-teal-700">{fmtAmt(pur)}</td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-indigo-300 bg-indigo-50 font-bold text-sm">
                        <td className="py-2 px-4 text-gray-800">Total</td>
                        <td className="py-2 px-4 text-right text-blue-800">{fmtAmt(summary.outputGst)}</td>
                        <td className="py-2 px-4 text-right text-teal-800">{fmtAmt(summary.purchaseGst)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tab 2: Sales */}
              {activeTab === "sales" && (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs text-gray-800">
                      <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 uppercase tracking-wide sticky top-0">
                        <tr>
                          {["#","Invoice No","Date","Customer","Mobile","HSN","Product","GST%","Taxable","CGST","SGST","IGST","Total GST","Invoice Total"].map((h) => (
                            <th key={h} className="py-2 px-3 text-right first:text-left">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {salesRows.length === 0 ? (
                          <tr><td colSpan={14} className="py-10 text-center text-gray-400">No sales data found</td></tr>
                        ) : salesRows.map((r, i) => (
                          <tr key={i} className={`border-t ${r.cancelled ? "bg-gray-100 line-through text-gray-400" : i % 2 === 0 ? "bg-white" : "bg-blue-50"} hover:bg-indigo-50`}>
                            <td className="py-2 px-3">{salesPage * PAGE_SIZE + i + 1}</td>
                            <td className="py-2 px-3 font-medium text-indigo-700">{r.invoiceNo || "—"}</td>
                            <td className="py-2 px-3 text-gray-500">{r.date || "—"}</td>
                            <td className="py-2 px-3">{r.customerName || "—"}</td>
                            <td className="py-2 px-3">{r.customerMobile || "—"}</td>
                            <td className="py-2 px-3">{r.hsnCode || "—"}</td>
                            <td className="py-2 px-3">{r.productName || "—"}</td>
                            <td className="py-2 px-3 text-right">{r.gstRate != null ? `${r.gstRate}%` : "—"}</td>
                            <td className="py-2 px-3 text-right">{fmtAmt(r.taxableAmount)}</td>
                            <td className="py-2 px-3 text-right text-blue-700">{fmtAmt(r.cgst)}</td>
                            <td className="py-2 px-3 text-right text-indigo-700">{fmtAmt(r.sgst)}</td>
                            <td className="py-2 px-3 text-right text-purple-700">{fmtAmt(r.igst)}</td>
                            <td className="py-2 px-3 text-right font-semibold text-teal-700">{fmtAmt(r.totalGst)}</td>
                            <td className="py-2 px-3 text-right font-semibold text-green-700">{fmtAmt(r.invoiceTotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <TotalRow rows={salesRows} span={8} cols={["taxableAmount","cgst","sgst","igst","totalGst","invoiceTotal"]} />
                    </table>
                  </div>
                  <Pagination page={salesPage} totalPages={report?.sales?.totalPages || 0} onChange={(p) => handlePageChange("sales", p)} />
                </>
              )}

              {/* Tab 3: Purchase */}
              {activeTab === "purchase" && (
                <>
                  <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-700">
                    <i className="fas fa-info-circle mr-1" />
                    Purchase GST is recorded from distributor invoices. ITC eligibility must be verified against GSTR-2A/2B.
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs text-gray-800">
                      <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 uppercase tracking-wide sticky top-0">
                        <tr>
                          {["#","Bill No","Date","Distributor","HSN","Product","GST%","Taxable","CGST","SGST","IGST","Total GST","Item Total"].map((h) => (
                            <th key={h} className="py-2 px-3 text-right first:text-left">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {purRows.length === 0 ? (
                          <tr><td colSpan={13} className="py-10 text-center text-gray-400">No purchase data found</td></tr>
                        ) : purRows.map((r, i) => (
                          <tr key={i} className={`border-t ${i % 2 === 0 ? "bg-white" : "bg-blue-50"} hover:bg-indigo-50`}>
                            <td className="py-2 px-3">{purchasePage * PAGE_SIZE + i + 1}</td>
                            <td className="py-2 px-3 font-medium text-indigo-700">{r.invoiceNo || "—"}</td>
                            <td className="py-2 px-3 text-gray-500">{r.date || "—"}</td>
                            <td className="py-2 px-3">{r.customerName || "—"}</td>
                            <td className="py-2 px-3">{r.hsnCode || "—"}</td>
                            <td className="py-2 px-3">{r.productName || "—"}</td>
                            <td className="py-2 px-3 text-right">{r.gstRate != null ? `${r.gstRate}%` : "—"}</td>
                            <td className="py-2 px-3 text-right">{fmtAmt(r.taxableAmount)}</td>
                            <td className="py-2 px-3 text-right text-blue-700">{fmtAmt(r.cgst)}</td>
                            <td className="py-2 px-3 text-right text-indigo-700">{fmtAmt(r.sgst)}</td>
                            <td className="py-2 px-3 text-right text-purple-700">{fmtAmt(r.igst)}</td>
                            <td className="py-2 px-3 text-right font-semibold text-teal-700">{fmtAmt(r.totalGst)}</td>
                            <td className="py-2 px-3 text-right font-semibold text-green-700">{fmtAmt(r.invoiceTotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <TotalRow rows={purRows} span={7} cols={["taxableAmount","cgst","sgst","igst","totalGst","invoiceTotal"]} />
                    </table>
                  </div>
                  <Pagination page={purchasePage} totalPages={report?.purchases?.totalPages || 0} onChange={(p) => handlePageChange("purchases", p)} />
                </>
              )}

              {/* Tab 4: Returns */}
              {activeTab === "returns" && (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs text-gray-800">
                      <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 uppercase tracking-wide sticky top-0">
                        <tr>
                          {["#","Return Ref","Date","Original Invoice","Customer","HSN","Product","GST%","Taxable","CGST","SGST","IGST","Total GST"].map((h) => (
                            <th key={h} className="py-2 px-3 text-right first:text-left">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {retRows.length === 0 ? (
                          <tr><td colSpan={13} className="py-10 text-center text-gray-400">No returns data found</td></tr>
                        ) : retRows.map((r, i) => (
                          <tr key={i} className={`border-t text-red-700 ${i % 2 === 0 ? "bg-red-50" : "bg-white"} hover:bg-red-100`}>
                            <td className="py-2 px-3">{returnsPage * PAGE_SIZE + i + 1}</td>
                            <td className="py-2 px-3 font-medium">{r.invoiceNo || "—"}</td>
                            <td className="py-2 px-3">{r.date || "—"}</td>
                            <td className="py-2 px-3">{r.originalInvoice || "—"}</td>
                            <td className="py-2 px-3">{r.customerName || "—"}</td>
                            <td className="py-2 px-3">{r.hsnCode || "—"}</td>
                            <td className="py-2 px-3">{r.productName || "—"}</td>
                            <td className="py-2 px-3 text-right">{r.gstRate != null ? `${r.gstRate}%` : "—"}</td>
                            <td className="py-2 px-3 text-right">{fmtAmt(Math.abs(r.taxableAmount))}</td>
                            <td className="py-2 px-3 text-right">{fmtAmt(Math.abs(r.cgst))}</td>
                            <td className="py-2 px-3 text-right">{fmtAmt(Math.abs(r.sgst))}</td>
                            <td className="py-2 px-3 text-right">{fmtAmt(Math.abs(r.igst))}</td>
                            <td className="py-2 px-3 text-right font-semibold">{fmtAmt(Math.abs(r.totalGst))}</td>
                          </tr>
                        ))}
                      </tbody>
                      <TotalRow rows={retRows.map((r) => ({
                        taxableAmount: Math.abs(r.taxableAmount), cgst: Math.abs(r.cgst),
                        sgst: Math.abs(r.sgst), igst: Math.abs(r.igst), totalGst: Math.abs(r.totalGst),
                      }))} span={8} cols={["taxableAmount","cgst","sgst","igst","totalGst"]} />
                    </table>
                  </div>
                  <Pagination page={returnsPage} totalPages={report?.returns?.totalPages || 0} onChange={(p) => handlePageChange("returns", p)} />
                </>
              )}

              {/* Tab 5: HSN/Rate */}
              {activeTab === "hsn" && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs text-gray-800">
                    <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 uppercase tracking-wide sticky top-0">
                      <tr>
                        {["HSN Code","Description","GST Rate","Total Qty","Taxable Amt","CGST","SGST","IGST","Total GST","Total Amt"].map((h) => (
                          <th key={h} className="py-2 px-3 text-right first:text-left">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {hsnRows.length === 0 ? (
                        <tr><td colSpan={10} className="py-10 text-center text-gray-400">No HSN data found</td></tr>
                      ) : hsnRows.map((r, i) => (
                        <tr key={i} className={`border-t ${i % 2 === 0 ? "bg-white" : "bg-blue-50"} hover:bg-indigo-50`}>
                          <td className="py-2 px-3 font-medium text-indigo-700">{r.hsnCode || "—"}</td>
                          <td className="py-2 px-3">{r.description || r.productName || "—"}</td>
                          <td className="py-2 px-3 text-right">{r.gstRate != null ? `${r.gstRate}%` : "—"}</td>
                          <td className="py-2 px-3 text-right">{r.quantity ?? "—"}</td>
                          <td className="py-2 px-3 text-right">{fmtAmt(r.taxableAmount)}</td>
                          <td className="py-2 px-3 text-right text-blue-700">{fmtAmt(r.cgst)}</td>
                          <td className="py-2 px-3 text-right text-indigo-700">{fmtAmt(r.sgst)}</td>
                          <td className="py-2 px-3 text-right text-purple-700">{fmtAmt(r.igst)}</td>
                          <td className="py-2 px-3 text-right font-semibold text-teal-700">{fmtAmt(r.totalGst)}</td>
                          <td className="py-2 px-3 text-right font-semibold text-green-700">{fmtAmt((r.taxableAmount || 0) + (r.totalGst || 0))}</td>
                        </tr>
                      ))}
                    </tbody>
                    <TotalRow rows={hsnRows.map((r) => ({
                      taxableAmount: r.taxableAmount, cgst: r.cgst, sgst: r.sgst,
                      igst: r.igst, totalGst: r.totalGst,
                      totalAmt: (r.taxableAmount || 0) + (r.totalGst || 0),
                    }))} span={4} cols={["taxableAmount","cgst","sgst","igst","totalGst","totalAmt"]} />
                  </table>
                </div>
              )}

            </div>
          </>
        )}

        {!report && !loading && (
          <div className="bg-white rounded-xl border border-blue-200 p-12 text-center">
            <i className="fas fa-file-invoice-dollar text-gray-300 text-5xl mb-3" />
            <p className="text-gray-500 text-sm">Select a date range and click Generate Report.</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default GstReport;
